import React, {FunctionComponent} from 'react';
import {Box, Chip, IconButton, Paper, Typography} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import HighlightText from '../../app-specific/highlight-text';
import CommandSearchResultIcon from './result-icon';
import {getCommandSearchLinkComponentProps, handleCommandSearchLinkClick} from './link-props';
import type {CommandSearchResult} from '../types';

interface CommandSearchResultCardProps {
    item: CommandSearchResult;
    selected: boolean;
    query: string;
    itemRef?: (element: HTMLElement | null) => void;
    onMouseEnter: () => void;
    onClick: () => void;
    onRememberResult?: (
        result: CommandSearchResult,
        actionKey?: string,
        actionPayload?: Record<string, string | number | boolean | undefined>,
    ) => void;
    onRemove?: () => void;
    removeLabel?: string;
}

/** Compact card tile for grid-layout command-search groups. */
const CommandSearchResultCard: FunctionComponent<CommandSearchResultCardProps> = ({
    item,
    selected,
    query,
    itemRef,
    onMouseEnter,
    onClick,
    onRememberResult,
    onRemove,
    removeLabel = 'Remove',
}) => {
    if (item.renderRow) {
        return (
            <Box ref={itemRef}>
                {item.renderRow({item, selected, query, onMouseEnter, onClick})}
            </Box>
        );
    }

    const isTutorial = item.category === 'tutorials';
    const hasLeadingVisual = Boolean(item.avatarUrl || item.icon);
    const iconSize = isTutorial ? 'tutorial' : 'card';
    const linkProps = getCommandSearchLinkComponentProps({
        to: item.to,
        target: item.target,
        rel: item.rel,
    });
    const paperComponentProps = item.to
        ? linkProps
        : {component: 'button' as const, type: 'button' as const};

    return (
        <Box ref={itemRef} sx={{position: 'relative', width: '100%'}}>
            <Paper
                {...paperComponentProps}
                onMouseEnter={onMouseEnter}
                onClick={(event) => {
                    const {runHandler, preventDefault} = handleCommandSearchLinkClick(event, {
                        to: item.to,
                        target: item.target,
                        rel: item.rel,
                    });
                    if (preventDefault) {
                        event.preventDefault();
                    }
                    if (runHandler) {
                        onClick();
                        return;
                    }
                    if (item.to) {
                        onRememberResult?.(item);
                    }
                }}
                elevation={0}
                sx={{
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    p: 1.25,
                    pr: onRemove ? 4.5 : 1.25,
                    borderRadius: 2,
                    border: 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                    transition: (theme) => theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
                        duration: theme.transitions.duration.shorter,
                    }),
                    '&:hover': {
                        bgcolor: selected ? 'action.selected' : 'action.hover',
                    },
                    display: 'flex',
                    flexDirection: isTutorial ? 'row' : 'column',
                    alignItems: isTutorial ? 'stretch' : 'flex-start',
                    gap: isTutorial ? 1.25 : 0.75,
                    minHeight: isTutorial
                        ? 72
                        : item.subtitle
                            ? (hasLeadingVisual ? 72 : 56)
                            : (hasLeadingVisual ? 56 : 48),
                    textDecoration: 'none',
                    color: 'inherit',
                    font: 'inherit',
                }}
            >
                {isTutorial ? (
                    <>
                        <CommandSearchResultIcon
                            icon={item.icon}
                            avatarUrl={item.avatarUrl}
                            size={iconSize}
                            useBizLearnFallback
                        />
                        <Box sx={{minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.35}}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    lineHeight: 1.35,
                                    color: 'text.primary',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                <HighlightText text={item.title} query={query}/>
                            </Typography>
                            {item.subtitle && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: 1.35,
                                    }}
                                >
                                    <HighlightText text={item.subtitle} query={query}/>
                                </Typography>
                            )}
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: 1,
                            }}
                        >
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: 1}}>
                                {hasLeadingVisual && (
                                    <CommandSearchResultIcon icon={item.icon} avatarUrl={item.avatarUrl} size="card"/>
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        lineHeight: 1.35,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        minWidth: 0,
                                        pt: hasLeadingVisual ? 0.25 : 0,
                                    }}
                                >
                                    <HighlightText text={item.title} query={query}/>
                                </Typography>
                            </Box>
                            {item.badge && (
                                <Chip
                                    label={item.badge}
                                    size="small"
                                    color={item.badgeColor ?? 'default'}
                                    variant={item.badgeColor ? 'filled' : 'outlined'}
                                    sx={{
                                        height: 20,
                                        flexShrink: 0,
                                        mt: 0.15,
                                        '& .MuiChip-label': {px: 0.75, fontSize: '0.65rem', fontWeight: 700},
                                    }}
                                />
                            )}
                        </Box>

                        {item.subtitle && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    lineHeight: 1.35,
                                    width: '100%',
                                    pl: hasLeadingVisual ? '40px' : 0,
                                }}
                            >
                                <HighlightText text={item.subtitle} query={query}/>
                            </Typography>
                        )}

                        {item.meta && (
                            <Typography variant="caption" color="text.disabled" sx={{fontWeight: 500, mt: 'auto'}}>
                                {item.meta}
                            </Typography>
                        )}
                    </>
                )}
            </Paper>
            {onRemove && (
                <IconButton
                    size="small"
                    aria-label={removeLabel}
                    title={removeLabel}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemove();
                    }}
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        color: 'text.secondary',
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': {
                            color: 'error.main',
                            bgcolor: 'background.paper',
                        },
                    }}
                >
                    <CloseRounded fontSize="inherit"/>
                </IconButton>
            )}
        </Box>
    );
};

export default CommandSearchResultCard;
